import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X, Trash2 } from "lucide-react";

export interface UploadError {
  photoIndex: number;
  photoPreview: string;
  error: string;
  fileName?: string;
}

export interface UploadErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: UploadError[];
  onRemovePhoto: (photoIndex: number) => void;
  isRetrying?: boolean;
}

export const UploadErrorModal: React.FC<UploadErrorModalProps> = ({
  isOpen,
  onClose,
  errors,
  onRemovePhoto,
  isRetrying = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Erro no Upload das Fotos
              </h2>
              <p className="text-sm text-gray-600">
                {errors.length} foto(s) com problema encontrada(s)
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {errors.map((error, index) => (
              <div
                key={`error-${error.photoIndex}-${index}`}
                className="flex items-start gap-4 p-4 bg-red-50 border border-red-200 rounded-lg"
              >
                {/* Photo Preview */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden border">
                    <img
                      src={error.photoPreview}
                      alt={`Foto ${error.photoIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Error Details */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 mb-1">
                    Foto {error.photoIndex + 1}
                    {error.fileName && (
                      <span className="text-gray-600 font-normal ml-1">
                        ({error.fileName})
                      </span>
                    )}
                  </h4>
                  <div className="flex items-start gap-2 flex-col">
                    <span className="text-sm text-red-600 mt-1">
                      A resolução mínima é de 80x80 pixels (moderação aws_rek)
                    </span>
                    <p className="text-xs text-gray-600 mb-3">{error.error}</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onRemovePhoto(error.photoIndex)}
                      disabled={isRetrying}
                      className="h-8 text-xs text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Remover foto
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Help Text */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              Dicas para resolver os problemas:
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>
                • Imagens muito pequenas: Use fotos com pelo menos 80x80 pixels
              </li>
              <li>
                • Arquivo muito grande: Reduza o tamanho para menos de 10MB
              </li>
              <li>• Formato inválido: Use apenas JPEG, PNG ou WEBP</li>
              <li>
                • Erro de processamento: Tente uma foto diferente ou
                redimensione
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <p className="text-sm text-gray-600">
            Remova as fotos com problema para continuar com a publicação
          </p>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={isRetrying}>
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
